import React, { useState, useEffect } from 'react';
import { ApCheckbox, ApIcon, colors } from '@alixpartners/ui-components';
import {
	Text,
	CellText,
	InputText,
	EditInput,
	TooltipContent,
	IconWrapper,
} from './TableRow.styles';
import {
	Cell,
	Row,
	CellNumber,
	SpanWrapper,
	RowSpan,
	SpanColumn,
	CheckboxWrapper,
	InfoIconWrapper,
} from '../PTItemsTable.styles';
import { getWorkingCapital, ITEMS_TYPE } from '../../../utils';
import { Tooltip } from 'react-tippy';

export const TableRow = ({
	companies,
	data,
	spendFormatter,
	onInputChange,
	onItemChange,
	highlightId,
	wacc,
	items,
	itemsType,
	showTopVendors,
}) => {
	const itemData = items.find(
		item => item.name === data.name && item.groupName === data.groupName
	);
	const combinedData = itemData ? { ...data, ...itemData } : data;
	const {
		name,
		spend,
		lowPercentage = 0,
		highPercentage = 0,
		targetDpo = 0,
		workingCapitalImpact,
		workingCapital,
		weightedAverageDpo,
		isUsed,
	} = combinedData;
	const [updates, setUpdates] = useState({
		lowPercentage,
		highPercentage,
		targetDpo,
	});

	useEffect(
		() => {
			if (itemData) {
				const {
					lowPercentage = 0,
					highPercentage = 0,
					targetDpo = 0,
				} = itemData;

				setUpdates({
					lowPercentage,
					highPercentage,
					targetDpo,
				});
			}
		},
		[items, itemData]
	);

	const onInputBlur = e => {
		const name = e.target.name;

		if (combinedData[name] !== updates[name]) {
			onInputChange({ id: data.id, ...updates });
		}
	};

	const onCellEdit = e => {
		const name = e.target.name;
		let number = Number(e.target.value);

		if (number > 100) {
			number = 100;
		}

		if (number < 0) {
			number = 0;
		}

		setUpdates({
			...updates,
			[name]: number,
		});
	};

	const onDpoChange = e => {
		const name = e.target.name;
		let number = Number(e.target.value);

		if (number < 0) {
			number = 0;
		}

		setUpdates({
			...updates,
			[name]: number,
		});
	};

	const getCompanyCellValue = companyName => {
		const spend = data.companies[companyName];
		let cellValue = spendFormatter(data.companies[companyName]);

		if (
			itemsType === ITEMS_TYPE.PT_HARMONIZATION &&
			spend &&
			data.companiesDpo
		) {
			const targetDpo = data.companiesDpo[companyName];
			cellValue = `${cellValue} (${targetDpo})`;
		}

		return cellValue;
	};

	const workingCapitalValue =
		itemsType === ITEMS_TYPE.PT_HARMONIZATION
			? workingCapital != null
				? workingCapital * (wacc / 100)
				: workingCapitalImpact
			: getWorkingCapital(combinedData, wacc);
	const wciLow = (workingCapitalValue * lowPercentage) / 100;
	const wciHigh = (workingCapitalValue * highPercentage) / 100;
	const isFaded = highlightId && data.id !== highlightId;
	const isSelected = items.some(
		node => node.name === data.name && node.groupName === data.groupName
	);
	const displayTopVendors = itemsType === ITEMS_TYPE.PT_IMPROVEMENT;

	return (
		<Row isFaded={isFaded} selected={isSelected}>
			<Cell title={name}>
				{displayTopVendors && (
					<InfoIconWrapper
						onClick={() => {
							showTopVendors(data);
						}}
					>
						<ApIcon
							iconName="outline_info"
							iconSize={16}
							iconColor={colors.grey5}
						/>
					</InfoIconWrapper>
				)}
				<CheckboxWrapper>
					<ApCheckbox
						checked={isSelected}
						disabled={isUsed}
						onClick={() => onItemChange(data)}
						onChange={() => {}}
					/>
				</CheckboxWrapper>
				<Text disabled={isUsed}>{name}</Text>
				{isUsed && (
					<IconWrapper>
						<Tooltip
							position="top"
							animation="fade"
							arrow
							html={
								<TooltipContent>
									This item can not be selected as it's already been included in
									a payment terms initiative
								</TooltipContent>
							}
							trigger="mouseenter"
						>
							<ApIcon
								iconName="baseline_help"
								iconSize={16}
								iconColor={colors.grey6}
							/>
						</Tooltip>
					</IconWrapper>
				)}
			</Cell>
			<CellNumber>{spendFormatter(spend)}</CellNumber>
			{companies.map((companyName, index) => (
				<CellNumber key={`${companyName}${index}`}>
					{getCompanyCellValue(companyName)}
				</CellNumber>
			))}

			{itemsType === ITEMS_TYPE.PT_IMPROVEMENT ? (
				<React.Fragment>
					<CellNumber>{weightedAverageDpo}</CellNumber>
					<CellNumber>
						<InputText>
							<EditInput
								name="targetDpo"
								value={String(updates.targetDpo)}
								maxLength={3}
								onChange={onDpoChange}
								onBlur={onInputBlur}
								type="number"
							/>
						</InputText>
					</CellNumber>
				</React.Fragment>
			) : (
				<CellNumber>{targetDpo}</CellNumber>
			)}

			<SpanWrapper>
				<RowSpan>
					<SpanColumn>
						<InputText>
							<EditInput
								name="lowPercentage"
								value={String(updates.lowPercentage)}
								maxLength={3}
								onChange={onCellEdit}
								onBlur={onInputBlur}
								type="number"
							/>
							%
						</InputText>
					</SpanColumn>
					<SpanColumn>
						<InputText>
							<EditInput
								name="highPercentage"
								value={String(updates.highPercentage)}
								maxLength={3}
								onChange={onCellEdit}
								onBlur={onInputBlur}
								type="number"
							/>
							%
						</InputText>
					</SpanColumn>
				</RowSpan>
			</SpanWrapper>
			<SpanWrapper>
				<RowSpan>
					<SpanColumn>
						<CellText>{spendFormatter(wciLow)}</CellText>
					</SpanColumn>
					<SpanColumn>
						<CellText>{spendFormatter(wciHigh)}</CellText>
					</SpanColumn>
				</RowSpan>
			</SpanWrapper>
		</Row>
	);
};

export default TableRow;
